import React, { useCallback } from 'react';
import { Box, Button, IconButton, Tooltip, Typography } from '@mui/material';
import { useTranslation, type TFunction } from 'react-i18next';
import ArrowUpwardOutlinedIcon from '@mui/icons-material/ArrowUpwardOutlined';
import ArrowDownwardOutlinedIcon from '@mui/icons-material/ArrowDownwardOutlined';
import DeleteOutlineOutlinedIcon from '@mui/icons-material/DeleteOutlineOutlined';
import WarningAmberOutlinedIcon from '@mui/icons-material/WarningAmberOutlined';
import BlockOutlinedIcon from '@mui/icons-material/BlockOutlined';
import EditNoteOutlinedIcon from '@mui/icons-material/EditNoteOutlined';
import DoorSlidingOutlinedIcon from '@mui/icons-material/DoorSlidingOutlined';

import { useMytaverse } from '@providers/MytaverseProvider';
import BrandingsService from '@services/BrandingsService';
import { MAP_LAYOUT_VERSION } from '@interfaces/map';

import { useEditorMode } from '../EditorModeContext';
import { useMapEditor } from '../MapEditorContext';
import { useActiveBrandingId } from '../useActiveBrandingId';
import { useCanUseMapEditor } from '../useCanUseMapEditor';
import {
  chainEndsInCap,
  findCatalogElement,
  hasBuiltGeometry,
  isGeometryMisconfigured,
} from '../mapEditorCatalog';
import { useMapEditorCatalog } from '../useMapEditorCatalog';
import {
  findRefusalAt,
  findStandingSpots,
  spawnLayoutInUnreal,
  type MapLayoutRefusalReason,
  type MapLayoutSpot,
} from '../mapEditorUnreal';
import { roomIdsOf, toPersistedRooms } from '../mapEditorRooms';
import { useInspectorStyles } from './styles';

/**
 * What to tell the author about a room the authority would not place.
 *
 * @param reason - Refusal reason from CURRENT_GEOMETRY_LIST.
 * @param translate - i18next `t`, passed in so this stays a plain function.
 * @returns A sentence naming both what happened and what to do about it.
 */
const describeRefusal = (
  reason: MapLayoutRefusalReason,
  translate: TFunction,
): string => {
  if (reason === 'overlap') {
    return translate(
      'editorMode.mapEditorRefusedOverlap',
      'Unreal refused this room — it lands on top of one already standing. Move or remove it, then Save again.',
    );
  }

  if (reason === 'chain-closed') {
    return translate(
      'editorMode.mapEditorRefusedClosed',
      'Never built — it comes after the wall that closes the layout. Move it above the end of the chain.',
    );
  }

  return translate(
    'editorMode.mapEditorRefusedTruncated',
    'Never attempted — the chain stopped at the refused room above it.',
  );
};

/**
 * Names how many brandable spots the authority found in a standing room.
 *
 * Reports what is in the world rather than what the element declares, which is the more useful
 * answer for a row that is already standing: a module placed twice is measured twice, and each copy
 * addresses its own spots. Absent is not zero — it means the room's level has not been read — and
 * one shipped module genuinely holds none, so the two are kept apart the same way the picker's
 * wall-layout step does.
 *
 * @param spots - Spots reported for this placement, or undefined when none were reported.
 * @param translate - i18next `t`, passed in so this stays a plain function.
 * @returns A short phrase for the row's subtitle, or empty when there is nothing to say yet.
 */
const describeStandingSpots = (
  spots: MapLayoutSpot[] | undefined,
  translate: TFunction,
): string => {
  // Nothing said at all before a report describes this row. "Spots unknown" belongs on an element
  // whose level the build could not read, not on every row of a layout that was just drafted.
  if (!spots) {
    return '';
  }

  if (!spots.length) {
    return translate(
      'editorMode.mapEditorWallLayoutEmpty',
      'No spots — empty walls',
    );
  }

  // Two explicit keys rather than i18next's count suffixes, matching the rest of the app.
  return spots.length === 1
    ? translate('editorMode.mapEditorSpotsCountSingular', '{{total}} spot', {
        total: spots.length,
      })
    : translate('editorMode.mapEditorSpotsCountPlural', '{{total}} spots', {
        total: spots.length,
      });
};

/**
 * The Layout panel: the chain of rooms this author has drafted, and the only place it is published.
 */
const MapEditorInspector = () => {
  const { t: translate } = useTranslation();
  const styles = useInspectorStyles();
  const {
    rooms,
    removeRoomAt,
    moveRoom,
    clearChain,
    markPublished,
    isDirty,
    refusals,
    revertDraft,
    canRevert,
    builtRooms,
  } = useMapEditor();
  const { sendMessageToUnreal } = useMytaverse();
  const currentBrandingId = useActiveBrandingId();
  const { setAutosaveStatus } = useEditorMode();
  const { elements: catalogElements, defaultEndElement } =
    useMapEditorCatalog();

  // The wall that closes the chain. Shown as a trailing row rather than folded into `rooms`,
  // because it is not the author's to move, remove or reorder — it is appended at publish time
  // and always goes last. Hiding it would leave them wondering where the extra room came from.
  //
  // Not shown for a chain that already ends in one, on the same rule the payload builder applies:
  // a restored layout can carry a cap as a picked room, and nothing is appended behind it.
  const endElement =
    defaultEndElement && !chainEndsInCap(roomIdsOf(rooms), catalogElements)
      ? findCatalogElement(catalogElements, defaultEndElement)
      : undefined;

  // The refusal that belongs to the cap, which nothing in the room list can carry a marker for.
  // Either Unreal flagged its own appended cap — `auto` marks nothing else — or it is the cap this
  // panel appended, whose index points past the last row the author wrote.
  //
  // That index has to be backed by the cap's own id. An index past the end of the list is not proof
  // on its own: a client that has not published in this session takes its chain from the rooms
  // Unreal reports *standing*, which is shorter than the chain the refusals were keyed against
  // whenever one was refused — so the refusal for an author's room would land past the end and be
  // reported here as the cap's, under a reason belonging to a different room.
  const capRefusal = isDirty
    ? undefined
    : refusals?.find(
        (refusal) =>
          refusal.auto === true ||
          (refusal.index >= rooms.length && refusal.id === defaultEndElement),
      );

  // Both greybox cases the author rows mark, on the one row where a greybox is the norm rather than
  // the exception: the shipped kit's cap has no room level of its own yet, so an uncommented cap row
  // promises a wall and delivers a grey box with nothing on screen to explain it.
  const isCapMisconfigured =
    !!endElement && isGeometryMisconfigured(endElement);
  const isCapMissingGeometry = !!endElement && !hasBuiltGeometry(endElement);

  // Gated on the map having a room kit at all, not just on the feature flag — see
  // useCanUseMapEditor. This panel does not render on a map without one, so the check is
  // here for the Save button rather than for the panel.
  const canSpawnLayout = useCanUseMapEditor();

  // The only thing in this panel that reaches the other participants. Everything else —
  // adding, removing, reordering, clearing — stays in this author's draft, which Unreal
  // renders locally as a preview, until they press it.
  //
  // Unreal is told first and the branding write follows. The spawn is what the author is
  // watching for, and holding it behind a round-trip would make Save feel broken on a slow
  // connection; a failed write costs the layout its persistence, not its publish.
  const handleSave = useCallback(() => {
    markPublished(rooms);
    // The catalog rides along so the authority does not have to resolve the ids itself — see
    // describeRoom in mapEditorUnreal. The cap id goes with it for the same reason: a packaged
    // server cannot read the catalog to resolve its own defaultEndElement.
    spawnLayoutInUnreal(
      sendMessageToUnreal,
      rooms,
      catalogElements,
      defaultEndElement,
    );

    if (!currentBrandingId) {
      return;
    }

    // Saved as the author's intent, not as what Unreal ended up building — those differ
    // whenever a room is refused for overlapping, and persisting the built list would
    // silently rewrite the layout the author authored.
    //
    // Each room goes out with the key it already has. Nothing is minted here: a Save that re-keyed
    // the layout would re-point every piece of content assigned to it.
    setAutosaveStatus('saving');
    BrandingsService.updateBranding(currentBrandingId, {
      layout: { version: MAP_LAYOUT_VERSION, rooms: toPersistedRooms(rooms) },
    })
      .then(() => setAutosaveStatus('saved'))
      .catch((saveError: Error) => {
        console.error(
          `Failed to save the map layout: ${saveError?.message}`,
          saveError,
        );
        setAutosaveStatus('error');
      });
  }, [
    sendMessageToUnreal,
    rooms,
    markPublished,
    currentBrandingId,
    setAutosaveStatus,
    catalogElements,
    defaultEndElement,
  ]);

  // Empties the draft only. Saving an empty draft is what tears the layout down for
  // everyone — SPAWN_LAYOUT carries clearExisting, so "nothing" publishes as a clear
  // and CLEAR_LAYOUT is not needed as a separate step.
  const handleClear = useCallback(() => {
    clearChain();
  }, [clearChain]);

  return (
    <Box>
      {rooms.length === 0 ? (
        <Typography sx={styles.emptyHint}>
          {translate(
            'editorMode.mapEditorEmptyChain',
            'No rooms yet. Use Add module on the left to build the floor plan.',
          )}
        </Typography>
      ) : (
        rooms.map((room, index) => {
          const { id, key } = room;
          const element = findCatalogElement(catalogElements, id);
          const name = element?.displayName ?? id;
          const isMissingGeometry = !!element && !hasBuiltGeometry(element);
          const isMisconfigured = !!element && isGeometryMisconfigured(element);

          // Only read while the draft still matches what was published. Refusals are keyed by
          // position in the chain that was sent, so the author's first edit shifts every row out
          // from under them — showing a stale report beats nothing, but pointing it at the wrong
          // room does not.
          const refusal = isDirty ? undefined : findRefusalAt(refusals, index);

          // Matched by placement key, not by position, so it stays right even while the draft is
          // dirty: it describes the room standing under this row's key, whatever row that is now.
          const spotsLabel = describeStandingSpots(
            findStandingSpots(builtRooms, key),
            translate,
          );

          return (
            // Keyed by the placement rather than by id-and-index, so a row keeps its React identity
            // when the author reorders or removes rooms around it.
            <Box key={key || `${id}-${index}`} sx={styles.mapEditorRow}>
              <Typography sx={styles.mapEditorRowIndex}>
                {String(index + 1).padStart(2, '0')}
              </Typography>
              <Box sx={styles.compactInfo}>
                <Typography sx={styles.compactTitle}>{name}</Typography>
                <Typography sx={styles.compactSubtitle}>
                  {spotsLabel ? `${id} · ${spotsLabel}` : id}
                </Typography>
              </Box>
              {/* A refusal outranks the catalog markers below: it reports what the authority
                  actually did with this row, where those only describe what the element declares.
                  A refused room is not standing at all, which is the more important thing to say. */}
              {refusal && (
                <Tooltip
                  title={describeRefusal(refusal.reason, translate)}
                  placement="left"
                  enterDelay={300}
                >
                  <BlockOutlinedIcon
                    sx={{
                      fontSize: 14,
                      // Amber for a truncated room: nothing is wrong with it, it simply never got
                      // its turn, and it goes away on its own once the overlap above is resolved.
                      color:
                        refusal.reason === 'overlap' ? '#FF6B6B' : '#FFB800',
                      flexShrink: 0,
                    }}
                    data-testid={`map-editor-refused-${index}`}
                  />
                </Tooltip>
              )}
              {!refusal && isMissingGeometry && (
                <Tooltip
                  title={
                    isMisconfigured
                      ? translate(
                          'editorMode.mapEditorGeometryUnavailable',
                          'This element names a room this build cannot load — Unreal falls back to a grey placeholder',
                        )
                      : translate(
                          'editorMode.mapEditorNoGeometry',
                          'This element has no room built yet — Unreal builds a grey placeholder sized to it',
                        )
                  }
                  placement="left"
                  enterDelay={300}
                >
                  {/* Red rather than amber for the misconfigured case: an unbuilt room is a
                      normal state an author can ignore, a room the build lost is not. */}
                  <WarningAmberOutlinedIcon
                    sx={{
                      fontSize: 14,
                      color: isMisconfigured ? '#FF6B6B' : '#FFB800',
                      flexShrink: 0,
                    }}
                    data-testid={
                      isMisconfigured
                        ? `map-editor-unavailable-${id}`
                        : `map-editor-placeholder-${id}`
                    }
                  />
                </Tooltip>
              )}
              <Box sx={styles.compactActions}>
                <IconButton
                  size="small"
                  sx={styles.iconSquareButton}
                  onClick={() => moveRoom(index, -1)}
                  disabled={index === 0}
                  aria-label={translate(
                    'editorMode.mapEditorMoveUp',
                    'Move up',
                  )}
                  data-testid={`map-editor-move-up-${index}`}
                >
                  <ArrowUpwardOutlinedIcon sx={{ fontSize: 15 }} />
                </IconButton>
                <IconButton
                  size="small"
                  sx={styles.iconSquareButton}
                  onClick={() => moveRoom(index, 1)}
                  disabled={index === rooms.length - 1}
                  aria-label={translate(
                    'editorMode.mapEditorMoveDown',
                    'Move down',
                  )}
                  data-testid={`map-editor-move-down-${index}`}
                >
                  <ArrowDownwardOutlinedIcon sx={{ fontSize: 15 }} />
                </IconButton>
                <IconButton
                  size="small"
                  sx={[styles.iconSquareButton, styles.iconDangerButton]}
                  onClick={() => removeRoomAt(index)}
                  aria-label={translate('editorMode.mapEditorRemove', 'Remove')}
                  data-testid={`map-editor-remove-${index}`}
                >
                  <DeleteOutlineOutlinedIcon sx={{ fontSize: 15 }} />
                </IconButton>
              </Box>
            </Box>
          );
        })
      )}

      {/* The cap, shown after the last picked room and outside `rooms` — see endElement above.
          Read-only: it has no move or remove control, because it is not a room the author chose
          and moving it anywhere but last would seal the chain early. */}
      {!!endElement && rooms.length > 0 && (
        <Box
          sx={[styles.mapEditorRow, styles.mapEditorEndRow]}
          data-testid="map-editor-end-cap-row"
        >
          <Typography sx={styles.mapEditorRowIndex}>
            <DoorSlidingOutlinedIcon sx={{ fontSize: 14 }} />
          </Typography>
          <Box sx={styles.compactInfo}>
            <Typography sx={styles.compactTitle}>
              {endElement.displayName}
            </Typography>
            <Typography sx={styles.compactSubtitle}>
              {translate(
                'editorMode.mapEditorEndCapHint',
                'Closes the layout — added automatically',
              )}
            </Typography>
          </Box>
          {capRefusal && (
            <Tooltip
              title={describeRefusal(capRefusal.reason, translate)}
              placement="left"
              enterDelay={300}
            >
              <BlockOutlinedIcon
                sx={{ fontSize: 14, color: '#FF6B6B', flexShrink: 0 }}
                data-testid="map-editor-end-cap-refused"
              />
            </Tooltip>
          )}
          {/* Same precedence as the author rows: a refusal outranks it, because a cap that was
              never placed has no geometry to describe. */}
          {!capRefusal && isCapMissingGeometry && (
            <Tooltip
              title={
                isCapMisconfigured
                  ? translate(
                      'editorMode.mapEditorGeometryUnavailable',
                      'This element names a room this build cannot load — Unreal falls back to a grey placeholder',
                    )
                  : translate(
                      'editorMode.mapEditorNoGeometry',
                      'This element has no room built yet — Unreal builds a grey placeholder sized to it',
                    )
              }
              placement="left"
              enterDelay={300}
            >
              <WarningAmberOutlinedIcon
                sx={{
                  fontSize: 14,
                  color: isCapMisconfigured ? '#FF6B6B' : '#FFB800',
                  flexShrink: 0,
                }}
                data-testid={
                  isCapMisconfigured
                    ? 'map-editor-end-cap-unavailable'
                    : 'map-editor-end-cap-placeholder'
                }
              />
            </Tooltip>
          )}
        </Box>
      )}

      {/* No layout-level warning about unbuilt elements. A greybox is the designed stand-in, not a
          fault: it keeps a freshly-seeded catalog walkable before any art exists. Counting them as
          problems made an intentional placeholder read as a spawn failure. The per-row marker below
          still says which specific element is a placeholder, which is the part an author acts on. */}

      {/* States plainly that the draft has not left this browser. Without it an author
          has no way to tell whether the room they just added is visible to everyone. */}
      {isDirty && (
        <Box sx={styles.mapEditorUnsavedBanner}>
          <EditNoteOutlinedIcon sx={{ fontSize: 14, color: '#AEB6FF' }} />
          <Typography sx={styles.mapEditorUnsavedText}>
            {/* An emptied draft needs its own wording. "Only you can see these changes" is true but
                unhelpful when the change is *nothing* and the rooms are still standing in front of
                you — Clear empties the draft, and it takes a Save to publish that as a teardown. */}
            {rooms.length === 0
              ? translate(
                  'editorMode.mapEditorUnsavedEmpty',
                  'Draft is empty. Any rooms still standing are published — Save to remove them for everyone.',
                )
              : translate(
                  'editorMode.mapEditorUnsaved',
                  'Unsaved — only you can see these changes. Save to apply them for everyone.',
                )}
          </Typography>
        </Box>
      )}

      <Box sx={styles.mapEditorFooter}>
        {/* Save is not gated on a non-empty chain: an empty draft is a valid layout —
            "nothing" — and saving it is how a standing layout gets torn down. Gating it
            would leave the author with no way to remove what they published. It is
            gated on isDirty, since re-sending an unchanged layout would make every
            other participant's rooms despawn and respawn for no reason. */}
        <Button
          variant="contained"
          size="small"
          sx={styles.mapEditorSaveButton}
          onClick={handleSave}
          disabled={!canSpawnLayout || !isDirty}
          data-testid="map-editor-save-btn"
        >
          {translate('editorMode.mapEditorSave', 'Save')}
        </Button>
        {/* The way back out of Clear. Enabled on any draft that differs from what is standing, not
            only on an emptied one — discarding a half-built draft is the same request. */}
        <Tooltip
          title={translate(
            'editorMode.mapEditorRevertHint',
            'Discard this draft and go back to the rooms that are standing',
          )}
          placement="top"
          enterDelay={300}
        >
          <Box component="span" sx={styles.mapEditorFooterAction}>
            <Button
              variant="outlined"
              size="small"
              sx={styles.spotActionButton}
              onClick={revertDraft}
              disabled={!canRevert}
              data-testid="map-editor-revert-btn"
            >
              {translate('editorMode.mapEditorRevert', 'Revert')}
            </Button>
          </Box>
        </Tooltip>
        <Button
          variant="outlined"
          size="small"
          sx={styles.spotActionButton}
          onClick={handleClear}
          disabled={!rooms.length}
          data-testid="map-editor-clear-btn"
        >
          {translate('editorMode.mapEditorClear', 'Clear')}
        </Button>
      </Box>

      {!canSpawnLayout && (
        <Typography sx={styles.mapEditorNote}>
          {translate(
            'editorMode.mapEditorFlagDisabled',
            'Layout building is turned off for this map.',
          )}
        </Typography>
      )}
    </Box>
  );
};

export default MapEditorInspector;
